import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { ButtonUI } from "../../../../../components/Button";
import type { User } from "../../../../../hooks/useOrganizationsRestApi";
import { AssignUserModal } from "./AssignUserModal";

interface UsersInOrganizationProps {
  users: User[];
  organizationId: string;
  organizationName: string;
  onRefresh: () => void;
}

export const UsersInOrganization: FC<UsersInOrganizationProps> = ({
  users,
  organizationId,
  organizationName,
  onRefresh,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const filteredUsers = users.filter(user => {
    const fullName = user.name || "";
    const kana = user.kana || "";

    return (
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kana.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(filteredUsers.map(user => user.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">所属ユーザー</h3>
            <p className="text-sm text-gray-500 mt-1">
              この組織に所属するユーザーの一覧です
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <ButtonUI
              onClick={onRefresh}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
            >
              更新
            </ButtonUI>
            <ButtonUI
              onClick={() => setIsAssignModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
            >
              + ユーザー追加
            </ButtonUI>
          </div>
        </div>
      </div>

      {/* 検索バー */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="ユーザー名、フリガナ、メールアドレスで検索"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="text-sm text-gray-600">
            {filteredUsers.length} / {users.length} 件
          </div>
        </div>
      </div>

      {/* ユーザー一覧 */}
      <div className="overflow-x-auto">
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 text-4xl mb-4">👤</div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? "検索結果が見つかりません" : "ユーザーがいません"}
            </h4>
            <p className="text-gray-600">
              {searchTerm
                ? "検索条件を変更してください"
                : "この組織にはまだユーザーが登録されていません"}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedUsers.size === filteredUsers.length &&
                      filteredUsers.length > 0
                    }
                    onChange={e => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ユーザー情報
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  メールアドレス
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ステータス
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map(user => (
                <tr
                  key={user.id}
                  className={`hover:bg-gray-50 ${
                    selectedUsers.has(user.id) ? "bg-blue-50" : ""
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={e =>
                        handleSelectUser(user.id, e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                          {user.name?.charAt(0) || "?"}
                        </div>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">{user.kana}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    <div className="text-sm text-gray-500">ID: {user.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        !user.disabled
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {!user.disabled ? "アクティブ" : "非アクティブ"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <Link
                      to={`/admin/organizations/${organizationId}/users/${user.id}`}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      詳細
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 選択したユーザーに対する一括操作 */}
      {selectedUsers.size > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">
              {selectedUsers.size}人のユーザーが選択されています
            </span>
            <div className="flex space-x-2">
              <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                組織移動
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ユーザー割り当てモーダル */}
      <AssignUserModal
        isOpen={isAssignModalOpen}
        organizationId={organizationId}
        organizationName={organizationName}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => {
          onRefresh();
          setIsAssignModalOpen(false);
        }}
      />
    </div>
  );
};
